import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';

export const getUserChats = createAsyncThunk("/getUserChats", async ({ accountId }, thunkAPI) => {
    try {
        const response = await api.post("/api/chats/fetchchats", { accountId });
        return response.data.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch chats';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const getUserChatMessages = createAsyncThunk("/getUserChatMessages", async ({ chatId }, thunkAPI) => {
    try {
        const response = await api.get(`/api/chats/${chatId}/messages`);
        return response.data.data;
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch chat messages';
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const sendMessage = createAsyncThunk("/sendMessage", async ({ chatId, text, accountId }, thunkAPI) => {
    try {
        const response = await api.post("/api/chats/send-message", { chatId, text, accountId });

        return {
            chatId,
            text,
            accountId,
            messageId: response.data.data.messageId,
            timestamp: new Date().toISOString(),
        };
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to send message";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

export const generateAIReferralDM = createAsyncThunk("/generateAIReferralDM", async ({ targetCompany, jobTitle, recipientName }, thunkAPI) => {
    try {
        const token = localStorage.getItem("token");

        const response = await api.post(
            "/api/chats/generate-referral-dm",
            { targetCompany, jobTitle, recipientName },
            {
                headers: { "auth-token": token }
            }
        );

        return response.data.message; // ✅ fixed
    } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || "Failed to regenerate message";
        return thunkAPI.rejectWithValue(errorMessage);
    }
});

const chatSlice = createSlice({
    name: "chatSlice",
    initialState: {
        chats: [],
        chatsLoading: false,
        chatsError: null,
        chatMessages: {},
        chatMessagesLoading: false,
        chatMessagesError: null,
        sendMessageLoading: false,
        sendMessageError: null,
        aiReferralMessage: null,
        aiReferralLoading: false,
        aiReferralError: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Get all chats of a user 
            .addCase(getUserChats.pending, (state) => {
                state.chatsLoading = true;
                state.chatsError = null;
            })
            .addCase(getUserChats.fulfilled, (state, action) => {
                state.chats = action.payload;
                state.chatsLoading = false;
            })
            .addCase(getUserChats.rejected, (state, action) => {
                state.chatsLoading = false;
                state.chatsError = action.payload;
            })

            // Get user chat messages
            .addCase(getUserChatMessages.pending, (state) => {
                state.chatMessagesLoading = true;
                state.chatMessagesError = null;
            })
            .addCase(getUserChatMessages.fulfilled, (state, action) => {
                const { chatId } = action.meta.arg;
                state.chatMessages[chatId] = action.payload;
                state.chatMessagesLoading = false;
            })
            .addCase(getUserChatMessages.rejected, (state, action) => {
                state.chatMessagesLoading = false;
                state.chatMessagesError = action.payload;
            })

            // Send message
            .addCase(sendMessage.pending, (state) => {
                state.sendMessageLoading = true;
                state.sendMessageError = null;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                const { chatId, text, accountId, messageId, timestamp } = action.payload;

                const newMessage = {
                    chatId,
                    text,
                    accountId,
                    senderId: accountId,
                    messageId,
                    timestamp,
                    isSender: true,
                    attachments: [],
                };

                const existingMessages = state.chatMessages[chatId] || [];
                state.chatMessages[chatId] = [...existingMessages, newMessage]; // ✅ append instead of prepend
                state.sendMessageLoading = false;
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.sendMessageLoading = false;
                state.sendMessageError = action.payload;
            })

            // Regenerate message with AI
            .addCase(generateAIReferralDM.pending, (state) => {
                state.aiReferralLoading = true;
                state.aiReferralError = null;
            })
            .addCase(generateAIReferralDM.fulfilled, (state, action) => {
                state.aiReferralMessage = action.payload;
                state.aiReferralLoading = false;
            })
            .addCase(generateAIReferralDM.rejected, (state, action) => {
                state.aiReferralLoading = false;
                state.aiReferralError = action.payload;
            });
    }
});

export default chatSlice.reducer;
